import type { HostPref, Project } from './protocol.ts';

export type ProjectCommand = { id: string; label: string; defaultPort?: number };

export type ProjectRow = Project & {
  exists: boolean;
  activeHost?: string;
  preferredHost?: HostPref;
  desktopOnline?: boolean;
  commands?: ProjectCommand[];
};

/** User-facing label for a host id (`server` or a desktop SSH hostname). */
export function displayHost(hostId: string): string {
  return hostId === 'server' ? 'server' : 'Desktop';
}

/** Last project the user picked. Home lists only this project's chats. */
export const PROJECT_KEY = 'selected-project';

export function readStoredProject(): string | null {
  return localStorage.getItem(PROJECT_KEY);
}

export function storeProject(id: string): void {
  localStorage.setItem(PROJECT_KEY, id);
}

/** Prefer the stored id when it still exists; otherwise the first project. */
export function resolveProjectId<T extends { id: string }>(
  projects: T[],
  stored: string | null,
): string | null {
  if (projects.length === 0) return null;
  if (stored && projects.some((p) => p.id === stored)) return stored;
  return projects[0]!.id;
}

/** Port the Commands tab last used for a desktop command. Shared with the home dev-server FAB. */
function portStorageKey(projectId: string, commandId: string): string {
  return `command-port:${projectId}:${commandId}`;
}

export function readStoredPort(projectId: string, commandId: string, fallback: number): string {
  try {
    return localStorage.getItem(portStorageKey(projectId, commandId)) ?? String(fallback);
  } catch {
    return String(fallback);
  }
}

export function storePort(projectId: string, commandId: string, value: string): void {
  try {
    localStorage.setItem(portStorageKey(projectId, commandId), value);
  } catch {
    // Quota or private mode: keep the in-memory value.
  }
}

export function parsePort(text: string): number | null {
  const n = Number(text.trim());
  if (!Number.isInteger(n) || n < 1 || n > 65535) return null;
  return n;
}
